import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as RightArrowIcon } from '../../../../../../assets/icons/arrow-right.svg';
import { ReactComponent as CensusSheetIcon } from '../../../../../../assets/icons/census-sheet.svg';
import { ReactComponent as CheckIcon } from '../../../../../../assets/icons/check.svg';
import { ReactComponent as CensusCheckGlyph } from '../../../../../../assets/icons/status-success.svg';
import { ReactComponent as TrashIcon } from '../../../../../../assets/icons/trash-new.svg';
import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import ExpandableCard from '../../../../../../shared/components/Cards/ExpandableCard/ExpandableCard';
import ExternalBenefitsDocs from '../../../../../../shared/components/ExternalBenefits/ExternalBenefitsDocs/ExternalBenefitsDocs';
import Badge from '../../../../../../shared/components/Globals/Badge/Badge';
import { Toast } from '../../../../../../shared/components/Globals/Toast/Toast';
import ActivationCensusModal, {
  type ActivationCensusApplyPayload
} from '../../../../../../shared/components/Modals/ActivationCensusModal/ActivationCensusModal';
import CensusBackgroundStatus from '../../../../../../shared/components/Modals/ActivationCensusModal/components/CensusBackgroundStatus/CensusBackgroundStatus';
import PayrollSection, {
  type IPayrollSchedule
} from '../../../../../../shared/components/PayrollSection/PayrollSection';
import SetupSection from '../../../../../../shared/components/SetupSection/SetupSection';
import SetupSlot from '../../../../../../shared/components/SetupSlot/SetupSlot';
import {
  ACTIVATION_REMOVE_CENSUS,
  ACTIVATION_UPLOAD_CENSUS,
  BENEFITS_DOCUMENTS_SOURCE,
  CLICK_ACTION
} from '../../../../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../../../../shared/constants/toast';
import { useActivationCensusFlow } from '../../../../../../shared/hooks/useActivationCensusFlow/useActivationCensusFlow';
import { useBroker } from '../../../../../../shared/hooks/useBroker/useBroker';
import { useUser } from '../../../../../../shared/hooks/useUser/useUser';
import type { IActivationCensusValidationErrorsResult, IQuote } from '../../../../../../shared/interfaces/quotes';
import QuotesService from '../../../../../../shared/services/api/quotes/quotes';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import { getActivationQuotes } from '../../../../../../shared/utils/checks/activationChecks';
import {
  buildActivationCensusPriceRequest,
  buildQuoteUpdateBody,
  censusVariantFor,
  findAppliedCensusFile,
  getPriceCalculationTotal,
  getQuoteTotal,
  isVoluntaryQuote
} from '../../../../../../shared/utils/quotes/quotes';

import './ActivationSetupCard.scss';

const SETUP = TRANSLATIONS_PROSPECTS.profile.activationSetup;

interface IQuoteSetup {
  censusFileName: string | null;
}

interface IActivationSetupCardProps {
  prospectId: number;
  isAppOnly?: boolean;
  requiresMinimum?: boolean;
  quotes: IQuote[];
  activeQuoteId?: number | null;
  appOnly?: boolean;
  onActiveQuoteChange?: (id: number) => void;
  onQuoteUpdate?: (quote: IQuote) => void;
  onQuotesRefetch?: () => void | Promise<void>;
  onCensusRemoved?: (quoteId: number) => void;
}

const ActivationSetupCard = ({
  prospectId,
  isAppOnly,
  requiresMinimum,
  quotes,
  activeQuoteId: controlledActiveQuoteId,
  appOnly = false,
  onActiveQuoteChange,
  onQuoteUpdate,
  onQuotesRefetch,
  onCensusRemoved
}: IActivationSetupCardProps) => {
  const { t } = useTranslation();
  const { brokerId } = useUser();
  const { addonTypeById, commercialAddons } = useBroker();

  const activationQuotes = useMemo(() => getActivationQuotes(quotes), [quotes]);
  const multipleQuotes = activationQuotes.length > 1;

  const isControlled = controlledActiveQuoteId !== undefined;
  const [internalActiveQuoteId, setInternalActiveQuoteId] = useState<number | null>(null);
  const effectiveActiveQuoteId = isControlled ? controlledActiveQuoteId : internalActiveQuoteId;
  const active = activationQuotes.find((q) => q.id === effectiveActiveQuoteId) ?? activationQuotes[0] ?? null;

  const handleSelectTab = (id: number) => {
    setCensusOpen(false);
    if (!isControlled) setInternalActiveQuoteId(id);
    onActiveQuoteChange?.(id);
  };
  const activeId = active?.id ?? 0;

  const [setupByQuote, setSetupByQuote] = useState<Record<number, IQuoteSetup>>({});

  const censusNameFor = (quote: IQuote): string | null => {
    const local = setupByQuote[quote.id];
    if (local) return local.censusFileName;
    return findAppliedCensusFile(quote)?.fileName ?? null;
  };

  const censusFileName = active ? censusNameFor(active) : null;
  const savedPayroll: IPayrollSchedule = {
    frequency: active?.frequency ?? '',
    lastPaymentDate: active?.paymentDate ?? ''
  };

  const isVoluntary = active ? isVoluntaryQuote(active, addonTypeById) : false;

  const [censusOpen, setCensusOpen] = useState(false);
  const [errorToastOpen, setErrorToastOpen] = useState(false);

  const resolvePriceMismatch = async (
    { summary, distribution }: IActivationCensusValidationErrorsResult,
    quoteId: number
  ) => {
    const quote = activationQuotes.find((q) => q.id === quoteId) ?? null;
    if (!quote || !brokerId || !distribution?.length) return null;

    const request = buildActivationCensusPriceRequest(quote, commercialAddons, distribution);
    if (Object.keys(request.products).length === 0) return null;

    const originalPrice = getQuoteTotal(quote);
    const newPrice = getPriceCalculationTotal(await QuotesService.getPriceCalculation(brokerId, request));
    const headcountDelta = summary.total - (quote.workforceSize ?? 0);
    if (headcountDelta === 0 && newPrice === originalPrice) return null;

    const { priceMismatchReasonMore, priceMismatchReasonFewer, priceMismatchReasonAges } = SETUP.censusModal;
    const reasonKey = headcountDelta > 0 ? priceMismatchReasonMore : priceMismatchReasonFewer;

    return {
      originalPrice,
      newPrice,
      reason: headcountDelta === 0 ? t(priceMismatchReasonAges) : t(reasonKey, { count: Math.abs(headcountDelta) })
    };
  };

  const quoteIds = useMemo(() => activationQuotes.map((quote) => quote.id), [activationQuotes]);
  const appliedQuoteIds = useMemo(
    () => activationQuotes.filter((quote) => findAppliedCensusFile(quote)).map((quote) => quote.id),
    [activationQuotes]
  );

  const censusFlow = useActivationCensusFlow({
    brokerId,
    prospectId,
    quoteId: activeId,
    quoteIds,
    appliedQuoteIds,
    requiresMinimum,
    variant: censusVariantFor(!!isAppOnly),
    resolvePriceMismatch
  });

  const patchSetup = (patch: Partial<IQuoteSetup>) =>
    setSetupByQuote((prev) => {
      const base = prev[activeId] ?? { censusFileName: null };
      return { ...prev, [activeId]: { ...base, ...patch } };
    });

  const isQuoteDone = (quote: IQuote): boolean => {
    const censusApplied = Boolean(censusNameFor(quote));
    const payrollDone = isVoluntaryQuote(quote, addonTypeById) ? Boolean(quote.frequency && quote.paymentDate) : true;
    return censusApplied && payrollDone;
  };

  const censusDone = censusFileName !== null;
  const allDone = activationQuotes.length > 0 && activationQuotes.every((q) => isQuoteDone(q));

  const handleSavePayroll = async (schedule: IPayrollSchedule) => {
    if (!active || !brokerId) return;
    const updated = await QuotesService.updateQuote(
      brokerId,
      prospectId,
      active.id,
      buildQuoteUpdateBody(active, {
        frequency: schedule.frequency || null,
        paymentDate: schedule.lastPaymentDate || null
      })
    );
    onQuoteUpdate?.(updated);
  };

  const handleApplyCensus = async ({ filename }: ActivationCensusApplyPayload) => {
    const distribution = censusFlow.distribution;

    if (!active || !brokerId || !distribution?.length) {
      setErrorToastOpen(true);
      return;
    }

    setErrorToastOpen(false);
    try {
      const updated = await QuotesService.applyActivationCensus(brokerId, prospectId, active.id, { distribution });

      if (onQuotesRefetch) await onQuotesRefetch();
      else onQuoteUpdate?.(updated);
      patchSetup({ censusFileName: filename });
      censusFlow.reset();
    } catch {
      setErrorToastOpen(true);
    }
  };

  const handleRemoveCensus = () => {
    patchSetup({ censusFileName: null });
    censusFlow.reset();
    onCensusRemoved?.(activeId);
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_REMOVE_CENSUS);
  };

  const handleOpenCensusModal = () => {
    setCensusOpen(true);
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_UPLOAD_CENSUS);
  };

  const handleResumeCensusModal = () => setCensusOpen(true);

  const badge = (
    <Badge variant={allDone ? 'success' : 'neutral'}>{allDone ? t(SETUP.badgeComplete) : t(SETUP.badgePending)}</Badge>
  );

  const showBackgroundStatus = !censusDone && !censusOpen && censusFlow.step !== 'upload';

  const renderCensusBody = () => {
    if (censusDone) {
      return (
        <SetupSlot
          icon={<CensusCheckGlyph width={18} height={18} />}
          description={t(SETUP.census.loaded, { count: active.workforceSize ?? 0, censusName: censusFileName })}
        >
          <CustomButton variant="default" size="xs" onClick={handleRemoveCensus}>
            <TrashIcon className="activation-setup__trash" />
            {t(SETUP.census.remove)}
          </CustomButton>
        </SetupSlot>
      );
    }

    if (showBackgroundStatus) {
      return <CensusBackgroundStatus step={censusFlow.step} onOpen={handleResumeCensusModal} />;
    }

    return (
      <SetupSlot icon={<CensusSheetIcon />} title={t(SETUP.census.intro)}>
        <CustomButton variant="primary" size="s" onClick={handleOpenCensusModal}>
          {t(SETUP.census.upload)}
          <RightArrowIcon className="activation-setup__arrow" />
        </CustomButton>
      </SetupSlot>
    );
  };

  if (activationQuotes.length === 0) return null;

  return (
    <>
      <ExpandableCard
        title={t(SETUP.title)}
        trailingComponent={badge}
        className="activation-setup"
        defaultExpanded={false}
      >
        <div className="activation-setup__body">
          {multipleQuotes && (
            <div className="activation-setup__tabs" role="tablist" aria-label={t(SETUP.title)}>
              {activationQuotes.map((quote) => {
                const isActive = quote.id === activeId;
                return (
                  <button
                    key={quote.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`activation-setup__tab${isActive ? ' activation-setup__tab--active' : ''}`}
                    onClick={() => handleSelectTab(quote.id)}
                  >
                    {quote.name}
                    {isQuoteDone(quote) && <CheckIcon className="activation-setup__tab-check" width={12} height={12} />}
                  </button>
                );
              })}
            </div>
          )}

          <SetupSection title={t(SETUP.census.title)} done={censusDone}>
            {renderCensusBody()}
          </SetupSection>

          {isVoluntary && <PayrollSection quoteId={activeId} saved={savedPayroll} onSave={handleSavePayroll} />}

          {appOnly && active && brokerId && (
            <ExternalBenefitsDocs
              layout="summary"
              quote={active}
              brokerId={brokerId}
              prospectId={prospectId}
              source={BENEFITS_DOCUMENTS_SOURCE.activationSetup}
              onSubmitted={(updated) => onQuoteUpdate?.(updated)}
            />
          )}
        </div>
      </ExpandableCard>

      <ActivationCensusModal
        open={censusOpen}
        brokerId={brokerId}
        flow={censusFlow}
        quoteName={multipleQuotes ? active?.name : undefined}
        isAppOnly={isAppOnly}
        onClose={() => setCensusOpen(false)}
        onApply={handleApplyCensus}
      />

      <Toast
        open={errorToastOpen}
        message={t(GENERIC_ERROR_TOAST.message)}
        variant={GENERIC_ERROR_TOAST.variant}
        onClose={() => setErrorToastOpen(false)}
      />
    </>
  );
};

export default ActivationSetupCard;
